// src/pages/PlotDetail.js
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import apiClient from "../utils/apiClient";
import SoilCompositionChart from "../components/SoilCompositionChart";
import PlantingForm from "../components/PlantingForm";
import PlotEventsTable from "../components/PlotEventsTable";
import FruitAssignmentForm from "../components/FruitAssignmentForm";
import CompostManagementForm from "../components/CompostManagementForm";

const PlotDetail = () => {
  const { garden_uuid, plot_uuid, year: yearParam } = useParams();
  const navigate = useNavigate();

  const initialYear = yearParam ? parseInt(yearParam, 10) : new Date().getFullYear();
  const [year, setYear] = useState(initialYear);

  const [plot, setPlot] = useState(null);
  const [plantedCrops, setPlantedCrops] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedPlanted, setSelectedPlanted] = useState(null);
  const [isEventModalOpen, setEventModalOpen] = useState(false);
  const [eventFormState, setEventFormState] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [plotRes, plantedRes] = await Promise.all([
        apiClient.get(`/plots/${plot_uuid}`),
        apiClient.get(`/gardens/${garden_uuid}/crops`),
      ]);

      setPlot(plotRes.data);
      const cropsForYearAndPlot = plantedRes.data.filter(
        (p) => {
          if (p.plot_uuid !== plot_uuid || !p.date_planted) return false;
          
          const plantedYear = new Date(p.date_planted).getFullYear();
          const cropTypeName = p.crop?.crop_type?.name?.toLowerCase();
          
          // Perennial crops (fruits and herbs) should be visible for all years after planting
          const isPerennial = cropTypeName === 'fruit' || cropTypeName === 'herb';
          
          if (isPerennial) {
            // Show perennial crops if they were planted in or before the selected year
            return plantedYear <= year;
          } else {
            // For annual crops (vegetables, etc.), only show crops planted in the selected year
            return plantedYear === year;
          }
        }
      );
      setPlantedCrops(cropsForYearAndPlot);
    } catch (err) {
      console.error("Failed to fetch plot details:", err);
    }
  }, [garden_uuid, plot_uuid, year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleYearChange = (newYear) => {
    setYear(newYear);
    navigate(`/gardens/${garden_uuid}/plots/${plot_uuid}/detail/${newYear}`);
  };

  const handleModalOpen = (planted = null) => {
    setSelectedPlanted(planted ? { ...planted } : null);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedPlanted(null);
  };

  const handleSave = async () => {
    setModalOpen(false);
    await fetchData();
  };

  const handleEventModalOpen = (event = null) => {
    setEventFormState(event);
    setEventModalOpen(true);
  };

  const handleEventModalClose = () => setEventModalOpen(false);

  const handleEventSave = async () => {
    setEventModalOpen(false);
    await fetchData();
  };

  const getSoilScoreAtDate = (date) => {
    if (!plot?.soil_compositions) return "N/A";
    const ts = new Date(date);
    const pastSnapshots = plot.soil_compositions
      .filter((sc) => new Date(sc.timestamp) <= ts)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return pastSnapshots.length > 0 ? pastSnapshots[0].score.toFixed(2) : "N/A";
  };

  const getHarvestWindow = (planted) => {
    const crop = planted.crop;
    if (!crop || !planted.date_planted) return "N/A";

    const plantedDate = new Date(planted.date_planted);
    const earliest = crop.harvest_min_days
      ? new Date(plantedDate.getTime() + crop.harvest_min_days * 86400000)
      : null;
    const latest = crop.harvest_max_days
      ? new Date(plantedDate.getTime() + crop.harvest_max_days * 86400000)
      : null;

    if (earliest && latest) {
      return `${earliest.toISOString().split("T")[0]} → ${latest
        .toISOString()
        .split("T")[0]}`;
    }
    if (earliest) return earliest.toISOString().split("T")[0];
    if (latest) return latest.toISOString().split("T")[0];
    return "N/A";
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Button
        variant="outlined"
        onClick={() => navigate(-1)}
        sx={{ marginBottom: 2 }}
      >
        Go Back
      </Button>

      {plot ? (
        <>
          {/* Plot Details Card - Always shown */}
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={4}>
              <Card sx={{ height: "100%" }}>
                <CardContent sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Button 
                      variant="outlined" 
                      onClick={() => handleYearChange(year - 1)}
                      className="vintage-button"
                      sx={{ minWidth: 'auto', padding: '8px 12px' }}
                    >
                      ☜
                    </Button>
                    <Typography variant="h5" sx={{ fontFamily: 'var(--font-decorative)', color: 'var(--color-sepia)', fontWeight: 600 }}>
                      {plot.name} – {year}
                    </Typography>
                    <Button 
                      variant="outlined" 
                      onClick={() => handleYearChange(year + 1)}
                      className="vintage-button"
                      sx={{ minWidth: 'auto', padding: '8px 12px' }}
                    >
                      ☞
                    </Button>
                  </Box>

                  <Typography variant="body1">
                    {plot.description || "No description available"}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Size: {plot.width} × {plot.depth} {plot.unit}
                  </Typography>
                  <Typography variant="body2">Type: {plot.plot_type?.name || plot.type}</Typography>
                  <Typography variant="body2">
                    Soil: {plot.soil_type?.name || "N/A"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Soil Chart - Only for Vegetable Beds */}
            {plot.plot_type?.name?.toLowerCase() === 'vegetable bed' && (
              <Grid item xs={12} md={8}>
                <Card sx={{ height: "100%" }}>
                  <CardContent sx={{ height: "100%" }}>
                    <SoilCompositionChart
                      data={plot.soil_compositions || []}
                      year={year}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Vegetable Bed specific content */}
          {plot.plot_type?.name?.toLowerCase() === 'vegetable bed' && (
            <>
              <Box sx={{ mt: 2, mb: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={() => handleModalOpen()}
                  className="vintage-button"
                >
                  ✚ Plan New Crop
                </Button>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Planted Crops
                </Typography>
                <TableContainer component={Paper} className="plot-planted-crops-table">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell className="column-crop">Crop</TableCell>
                        <TableCell className="column-date-planted">Date Planted</TableCell>
                        <TableCell className="column-rows">Rows</TableCell>
                        <TableCell className="column-amount">Amount</TableCell>
                        <TableCell className="column-harvest">Expected Harvest</TableCell>
                        <TableCell className="column-soil-impact">Soil Impact</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {plantedCrops.map((planted) => (
                        <TableRow
                          key={planted.planted_uuid}
                          onClick={() => handleModalOpen(planted)}
                          className="clickable-row"
                        >
                          <TableCell className="column-crop">{planted.crop.name}</TableCell>
                          <TableCell className="column-date-planted">{planted.date_planted}</TableCell>
                          <TableCell className="column-rows">{planted.rows}</TableCell>
                          <TableCell className="column-amount">{planted.amount}</TableCell>
                          <TableCell className="column-harvest">{getHarvestWindow(planted)}</TableCell>
                          <TableCell className="column-soil-impact">{getSoilScoreAtDate(planted.date_planted)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <PlantingForm
                plots={[plot]}
                garden_uuid={garden_uuid}
                planted={selectedPlanted}
                open={isModalOpen}
                onClose={handleModalClose}
                onSave={handleSave}
                year={year}
              />
            </>
          )}

          {/* Tasks Section - For all plot types */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Tasks
            </Typography>
            <PlotEventsTable
              plot_uuid={plot_uuid}
              year={year}
              onEditEvent={handleEventModalOpen}
            />
          </Box>
        </>
      ) : (
        <Typography>Loading...</Typography>
      )}
    </Box>
  );
};

export default PlotDetail;
